import React, { useState, useEffect } from "react";
import {
    Button, Card, Box, Select, MenuItem, FormControl,
    Grid, TextField, Typography, Checkbox, CircularProgress,
    Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
    Collapse, InputAdornment, Avatar, Chip, Tooltip, IconButton
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AttendenceService from "./Locality/Service/attendenceService";
import StudentService from "./Locality/Service/studentService";
import AddClassService from "./Locality/Service/addClassService";
import SectionService from "./Locality/Service/sectionService";
import CalenderAssignmentService from "./Locality/Service/calenderAssignmentService";
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import FilterListIcon from '@material-ui/icons/FilterList';
import CloseIcon from '@material-ui/icons/Close';
import EventIcon from '@material-ui/icons/Event';
import SchoolIcon from '@material-ui/icons/School';
import PersonIcon from '@material-ui/icons/Person';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import EditIcon from '@material-ui/icons/Edit';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

const getCreatedByLabel = (createdBy) => {
    if (!createdBy) return '—';
    if (createdBy.teacher?.teacherName) return createdBy.teacher.teacherName;
    if (createdBy.school?.schooleName) return createdBy.school.schooleName;
    if (Array.isArray(createdBy.students) && createdBy.students.length > 0) {
        const names = createdBy.students.map((s) => s?.firstName).filter(Boolean);
        if (names.length) return names.join(', ');
    }
    return createdBy.userName || createdBy.serialNumber || '—';
};

const toDateKey = (value) => {
    if (value == null || value === '') return '';
    const s = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    return s;
};

const resolveId = (value) => {
    if (!value) return '';
    if (typeof value === 'object') return String(value._id || value.id || '');
    return String(value);
};

/** School-wide or class-scoped holiday from calendar (Excel / manual). */
const findHolidayOnDate = (holidays, isoDate, classId) => {
    const dateKey = toDateKey(isoDate);
    if (!dateKey || !Array.isArray(holidays)) return null;
    const classKey = resolveId(classId);
    return (
        holidays.find((row) => {
            if (String(row?.eventType || '').toLowerCase() !== 'holiday') return false;
            if (toDateKey(row?.date) !== dateKey) return false;
            if (row.isEntireSchool || row.isEventForAllStudent) return true;
            const holidayClassId = resolveId(row.classId);
            if (!holidayClassId) return true;
            if (!classKey) return true;
            return holidayClassId === classKey;
        }) || null
    );
};

const useStyles = makeStyles((theme) => ({
    pageHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    pageTitle: {
        fontWeight: 700,
        fontSize: '1.9rem',
        color: '#0f2d4d',
        lineHeight: 1.2,
    },
    pageSubtitle: {
        color: '#6b7280',
        fontSize: '0.9rem',
        marginTop: 2,
    },
    headerStat: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 92,
        padding: theme.spacing(1, 2),
        borderRadius: 12,
        border: '1px solid #e6eaf0',
        backgroundColor: '#fff',
    },
    statValue: {
        fontWeight: 700,
        fontSize: '1.4rem',
        lineHeight: 1,
    },
    statLabel: {
        fontSize: '0.72rem',
        color: '#9aa6b6',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginTop: 4,
    },
    noteBanner: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.5, 2),
        borderRadius: 12,
        backgroundColor: '#eef4ff',
        border: '1px solid #d6e4ff',
        marginBottom: theme.spacing(2),
    },
    noteIconWrap: {
        width: 28,
        height: 28,
        borderRadius: '50%',
        backgroundColor: '#1769d6',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    filtersCard: {
        borderRadius: 14,
        border: '1px solid #e6eaf0',
        boxShadow: '0 2px 10px rgba(16,42,67,0.06)',
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    filtersTopRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
    },
    filtersChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        borderRadius: 8,
        border: '1px solid #d9dee7',
        backgroundColor: '#f7f9fc',
        cursor: 'pointer',
        fontWeight: 600,
        color: '#0f2d4d',
        userSelect: 'none',
    },
    countBadge: {
        backgroundColor: '#fff',
        color: '#1769d6',
        border: '1px solid #cfe0fb',
        borderRadius: 12,
        padding: '1px 10px',
        fontSize: '0.72rem',
        fontWeight: 700,
        marginLeft: 8,
    },
    fieldLabel: {
        fontSize: '0.78rem',
        fontWeight: 600,
        color: '#46566b',
        marginBottom: 6,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        minHeight: 18,
    },
    filterFieldWrap: {
        display: 'flex',
        flexDirection: 'column',
    },
    filterHelperSlot: {
        minHeight: 20,
        marginTop: 6,
        fontSize: '0.72rem',
        lineHeight: 1.3,
    },
    filterInput: {
        width: '100%',
        margin: 0,
        '& .MuiOutlinedInput-root': {
            height: 40,
        },
        '& .MuiOutlinedInput-input': {
            padding: '8px 10px',
            fontSize: '0.8125rem',
        },
    },
    filterSelect: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            height: 40,
        },
    },
    primaryBtn: {
        backgroundColor: '#1769d6',
        color: '#fff',
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        padding: '8px 18px',
        boxShadow: '0 4px 10px rgba(23,105,214,0.25)',
        '&:hover': { backgroundColor: '#125bbd' },
    },
    darkBtn: {
        backgroundColor: '#0f2d4d',
        color: '#fff',
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        padding: '8px 18px',
        '&:hover': { backgroundColor: '#0b2238' },
    },
    outlinedBtn: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        color: '#46566b',
        border: '1px solid #d9dee7',
    },
    tableCard: {
        borderRadius: 14,
        border: '1px solid #e6eaf0',
        boxShadow: '0 2px 10px rgba(16,42,67,0.06)',
        overflow: 'hidden',
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2),
    },
    iconBtnOutlined: {
        border: '1px solid #d9dee7',
        borderRadius: 8,
        padding: 8,
        color: '#46566b',
    },
    studentAvatar: {
        width: 38,
        height: 38,
        backgroundColor: '#eef4ff',
        color: '#1769d6',
        fontSize: '0.95rem',
    },
    presentChip: {
        backgroundColor: '#e6f7ed',
        color: '#1a9b54',
        fontWeight: 600,
        borderRadius: 6,
    },
    absentChip: {
        backgroundColor: '#ffebee',
        color: '#c62828',
        fontWeight: 600,
        borderRadius: 6,
    },
    footerBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2),
        borderTop: '1px solid #eef1f5',
    },
}));

export default function Attendance() {
    const classes = useStyles();
    const [dateValue, setDateValue] = useState(new Date().toISOString().split("T")[0]);
    const [classValue, setClassValue] = useState("");
    const [sectionValue, setSectionValue] = useState("");
    const [sectionList, setSectionList] = useState([]);
    const [studentList, setStudentList] = useState([]);
    const [addClassList, setAddClassList] = useState([]);
    const [absentStudentsList, setAbsentStudentsList] = useState([]);
const [showAbsentList, setShowAbsentList] = useState(false);
    const [todayAttendanceSummary, setTodayAttendanceSummary] = useState({attendenceReport:[]});
    const [todayAttendanceId, setTodayAttendanceId] = useState('');
    const [todayAttendanceCreatedBy, setTodayAttendanceCreatedBy] = useState(null);
    const [todayAttendanceEditable, setTodayAttendanceEditable] = useState([]);
    const [isTodayEditMode, setIsTodayEditMode] = useState(false);
    const [todayEditSaving, setTodayEditSaving] = useState(false);
    var [classId, setClassId] = useState("");
    const [submitLoading, setSubmitLoading] = useState(false);
    // ---- Redesigned UI state ----
    const [noteOpen, setNoteOpen] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
    const [weekendAlertedForDate, setWeekendAlertedForDate] = useState('');
    const [holidayAlertedForDate, setHolidayAlertedForDate] = useState('');
    const [schoolHolidays, setSchoolHolidays] = useState([]);
    const isWeekend = (isoDate) => {
        try {
            const d = new Date(`${isoDate}T00:00:00`);
            const day = d.getDay(); // 0=Sun, 6=Sat
            return day === 0 || day === 6;
        } catch {
            return false;
        }
    };
    const holidayForSelectedDate = findHolidayOnDate(schoolHolidays, dateValue, classValue);
    const isHoliday = Boolean(holidayForSelectedDate);
    const holidayLabel = String(holidayForSelectedDate?.eventName || 'Holiday').trim() || 'Holiday';
    const isAttendanceBlocked = isWeekend(dateValue) || isHoliday;

    useEffect(() => {
        getAddClassList();
        if (userDetails.schooleId) {
            CalenderAssignmentService.getAllCalenderAssignment({
                schooleId: userDetails.schooleId,
                eventType: 'holiday',
            })
                .then((res) => {
                    setSchoolHolidays(Array.isArray(res) ? res : []);
                })
                .catch(() => setSchoolHolidays([]));
        }
    }, []);
    

    const getSectionListByClass = (classId) => {
        if (!classId) {
            setSectionList([]);
            return;
        }
        SectionService.findSectionList({
            schooleId: userDetails.schooleId,
            classId,
        }).then((res) => {
            let list = Array.isArray(res) ? res : [];
            setSectionList(list);
            if (list.length === 0) {
                setSectionValue("");
            } else if (userDetails.role === "TEACHER" && userDetails.sectionId) {
                setSectionValue(userDetails.sectionId);
            } else {
                setSectionValue("");
            }
        }).catch(() => {
            setSectionList([]);
            setSectionValue("");
        });
    };

    const fetchStudentList = (classId, newDate, sectionId) => {
        const today = newDate ? newDate : dateValue;
        const body = { classId };
        if (sectionId) body.sectionId = sectionId;
        StudentService.getAllStudentById(userDetails.schooleId, body).then((res) => {
            setStudentList(res.map(student => ({ ...student, status: true, lateComing: false, earlyGoing: false })));
            getTodayAttendanceSummary(classId, today, res, sectionId);
        }).catch((err) => {
            console.error(err);
        });
    };

    const getAddClassList = () => {
        AddClassService.getAllAddClass(userDetails.schooleId).then((res) => {
            if (userDetails.role === "TEACHER") {
                const teacherClass = res.find(c => c._id === userDetails.classId);
                if (teacherClass) {
                    setAddClassList(res);
                    setClassValue(teacherClass._id);
                    if (userDetails.sectionId) setSectionValue(userDetails.sectionId);
                    getSectionListByClass(teacherClass._id);
                    fetchStudentList(teacherClass._id, dateValue, userDetails.sectionId || undefined);
                }
            } else {
                setAddClassList(res);
            }
        }).catch((err) => {
            console.error(err);
        });
    };

const getStudentList = (event) => {
    const selectedClassId = event.target.value;
    setClassValue(selectedClassId);
    setSectionValue("");
    getSectionListByClass(selectedClassId);
    if (selectedClassId) {
        fetchStudentList(selectedClassId, dateValue, undefined);
    } else {
        setStudentList([]);
        setSectionList([]);
    }
};

const getSectionChange = (event) => {
    const selectedSectionId = event.target.value;
    setSectionValue(selectedSectionId);
    if (classValue) {
        fetchStudentList(classValue, dateValue, selectedSectionId || undefined);
    }
};

    const getTodayAttendanceSummary = (selectedClassId, selectedDate, studentList, sectionId) => {
        const keys = {
            schooleId: userDetails.schooleId,
            classId: selectedClassId,
            date: selectedDate || new Date().toISOString().split("T")[0]
        };
        if (sectionId) keys.sectionId = sectionId;

        AttendenceService.getTodaySummary(keys).then((res) => {
 
            setTodayAttendanceSummary(res);
            // also find the actual Attendence document id for edit/save
            AttendenceService.findAttendenceList(keys).then((rows) => {
                const first = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
                setTodayAttendanceId(first?._id || '');
                setTodayAttendanceCreatedBy(first?.createdBy || null);
            }).catch(() => {
                setTodayAttendanceId('');
                setTodayAttendanceCreatedBy(null);
            });

            const reportByStudentId = new Map(
                (res.attendenceReport || []).map((r) => [String(r.studentId), r]),
            );
            const updatedStudentList = studentList.map((student) => {
                const rep = reportByStudentId.get(String(student._id));
                if (!rep) return { ...student, status: true, lateComing: false, earlyGoing: false };
                return {
                    ...student,
                    status: Boolean(rep.status),
                    lateComing: Boolean(rep.lateComing),
                    earlyGoing: Boolean(rep.earlyGoing),
                };
            });

        setStudentList(updatedStudentList);
        setIsTodayEditMode(false);
        setTodayAttendanceEditable([]);

        }).catch((err) => {
            console.error(err);
            setTodayAttendanceCreatedBy(null);
        });
    }

    const hasMarkedAttendance = (todayAttendanceSummary.attendenceReport || []).length > 0;
    const startTodayEdit = () => {
        // Enable editing using the existing Student Details list (single edit flow)
        setIsTodayEditMode(true);
    };

    const cancelTodayEdit = () => {
        setIsTodayEditMode(false);
        // reload original values from summary to revert edits
        if (classValue) fetchStudentList(classValue, dateValue, sectionValue || undefined);
    };

    const saveTodayEdit = async () => {
        if (!todayAttendanceId) {
            alert("Unable to edit: attendance id not found for today.");
            return;
        }
        setTodayEditSaving(true);
        try {
            const payloadList = (studentList || []).map((student) => ({
                studentId: student._id,
                studentName: `${student.firstName || ""} ${student.lastName || ""}`.trim(),
                status: Boolean(student.status),
                late: Boolean(student.lateComing),
                early: Boolean(student.earlyGoing),
            }));
            const body = {
                _id: todayAttendanceId,
                schooleId: userDetails.schooleId,
                teacherId: userDetails._id,
                date: dateValue,
                studentList: payloadList,
            };
            await AttendenceService.upadeAttendence(body);
            alert("Attendance Updated Successfully");
            if (classValue) {
                fetchStudentList(classValue, dateValue, sectionValue || undefined);
            }
            cancelTodayEdit();
        } catch (e) {
            alert(e?.response?.data?.message || e?.message || "Error updating attendance");
        } finally {
            setTodayEditSaving(false);
        }
    };
    const handleDateChange = (e) => {
  const newDate = e.target.value;
  setDateValue(newDate);
  if (isWeekend(newDate) && weekendAlertedForDate !== newDate) {
    alert("Weekend selected (Saturday/Sunday). Attendance cannot be added.");
    setWeekendAlertedForDate(newDate);
  } else {
    const holiday = findHolidayOnDate(schoolHolidays, newDate, classValue);
    if (holiday && holidayAlertedForDate !== newDate) {
      const name = String(holiday.eventName || 'Holiday').trim() || 'Holiday';
      alert(`Holiday selected (${name}). Attendance cannot be added.`);
      setHolidayAlertedForDate(newDate);
    }
  }
  if (classValue) {
    fetchStudentList(classValue, newDate, sectionValue || undefined);
  }
};
    const handleCheck = (studentId) => {
        setStudentList(prev => prev.map(student =>
            student._id === studentId ? { ...student, status: !student.status } : student
        ));
    };

    const handleLateComing = (studentId) => {
        setStudentList(prev => prev.map(student =>
            student._id === studentId ? { ...student, lateComing: !student.lateComing } : student
        ));
    };

    const handleEarlyGoing = (studentId) => {
        setStudentList(prev => prev.map(student =>
            student._id === studentId ? { ...student, earlyGoing: !student.earlyGoing } : student
        ));
    };

    const editable = !hasMarkedAttendance || isTodayEditMode;

    const filteredStudents = studentList.filter((s) =>
        `${s.firstName || ""} ${s.lastName || ""}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const presentCount = studentList.filter((s) => s.status).length;
    const absentCount = studentList.length - presentCount;

    const activeFilterCount = [classValue, sectionValue].filter(Boolean).length;

    const allFilteredPresent = filteredStudents.length > 0 && filteredStudents.every((s) => s.status);
    const someFilteredPresent = filteredStudents.some((s) => s.status) && !allFilteredPresent;

    const handleSelectAll = () => {
        if (!editable) return;
        const newVal = !allFilteredPresent;
        const ids = new Set(filteredStudents.map((s) => s._id));
        setStudentList((prev) => prev.map((s) => (
            ids.has(s._id)
                ? { ...s, status: newVal, lateComing: newVal ? s.lateComing : false, earlyGoing: newVal ? s.earlyGoing : false }
                : s
        )));
    };

    const handleResetFilters = () => {
        setSectionValue("");
        setSearchTerm("");
        if (classValue) fetchStudentList(classValue, dateValue, undefined);
    };

    const headerCellStyle = {
        backgroundColor: '#f4f6fa',
        color: '#46566b',
        fontWeight: 700,
        fontSize: '0.8rem',
        borderBottom: '1px solid #e6eaf0',
    };

    const handleSubmit = () => {
        if (!classValue) {
            alert("Please select a class.");
            return;
        }
        if (isWeekend(dateValue)) {
            alert("Attendance cannot be added on Saturday or Sunday.");
            return;
        }
        const holiday = findHolidayOnDate(schoolHolidays, dateValue, classValue);
        if (holiday) {
            const name = String(holiday.eventName || 'Holiday').trim() || 'Holiday';
            alert(`Attendance cannot be added on a holiday (${name}).`);
            return;
        }
        const studentListPayload = studentList.map((student) => ({
            studentId: student._id,
            studentName: `${student.firstName || ""} ${student.lastName || ""}`.trim(),
            status: student.status,
            late: Boolean(student.lateComing),
            early: Boolean(student.earlyGoing),
        }));
        const attendanceData = {
            classId: classValue,
            schooleId: userDetails.schooleId,
            sectionId: sectionValue || undefined,
            studentList: studentListPayload,
            teacherId: userDetails._id,
            date: dateValue
        };
        setSubmitLoading(true);
        AttendenceService.creteAttendence(attendanceData).then(() => {
            setSubmitLoading(false);
            alert("Attendance Submitted Successfully");
            fetchStudentList(classValue, dateValue, sectionValue || undefined);
        }).catch(err => {
            setSubmitLoading(false);
            alert(err.response?.data?.message || err.message || "Error submitting attendance");
        });
    };

    return (
        <>
            {/* Page header */}
            <Box className={classes.pageHeader}>
                <Box>
                    <Typography className={classes.pageTitle}>Attendance</Typography>
                    <Typography className={classes.pageSubtitle}>
                        Mark and review daily student attendance.
                    </Typography>
                </Box>
                {studentList.length > 0 && (
                    <Box display="flex" gridGap={12} flexWrap="wrap">
                        <Box className={classes.headerStat}>
                            <span className={classes.statValue} style={{ color: '#1a9b54' }}>{presentCount}</span>
                            <span className={classes.statLabel}>Present</span>
                        </Box>
                        <Box className={classes.headerStat}>
                            <span className={classes.statValue} style={{ color: '#c62828' }}>{absentCount}</span>
                            <span className={classes.statLabel}>Absent</span>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Dismissible note */}
            {noteOpen && (
                <Box className={classes.noteBanner}>
                    <span className={classes.noteIconWrap}>
                        <InfoOutlinedIcon style={{ fontSize: 18 }} />
                    </span>
                    <Box flex={1}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#0f2d4d' }}>Note</Typography>
                        <Typography variant="body2" style={{ color: '#46566b' }}>
                            Tick the <b>Present</b> checkbox for students who attended, then add <b>Late</b> / <b>Early</b> where needed and submit.
                            {isWeekend(dateValue) ? ' Weekend (Sat/Sun) selected — attendance entry is disabled.' : ''}
                            {isHoliday ? ` Holiday (${holidayLabel}) selected — attendance entry is disabled.` : ''}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setNoteOpen(false)} aria-label="dismiss note">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}

            {/* Filters card */}
            <Card className={classes.filtersCard} elevation={0}>
                <Box className={classes.filtersTopRow}>
                    <Box
                        className={classes.filtersChip}
                        onClick={() => setFiltersOpen((o) => !o)}
                        role="button"
                        tabIndex={0}
                    >
                        <FilterListIcon fontSize="small" />
                        <span>Filters</span>
                        <span className={classes.countBadge}>{activeFilterCount}</span>
                        {filtersOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                    </Box>
                </Box>

                <Collapse in={filtersOpen}>
                    <Box mt={2}>
                        <Grid container spacing={2} alignItems="flex-start">
                            <Grid item xs={12} sm={6} md={3}>
                                <Box className={classes.filterFieldWrap}>
                                    <Typography className={classes.fieldLabel}>
                                        <EventIcon style={{ fontSize: 16 }} /> Date
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        type="date"
                                        className={classes.filterInput}
                                        value={dateValue}
                                        onChange={handleDateChange}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <Typography
                                        className={classes.filterHelperSlot}
                                        style={{ color: isAttendanceBlocked ? '#c62828' : 'transparent' }}
                                    >
                                        {isWeekend(dateValue)
                                            ? 'Weekend (Sat/Sun): entry disabled.'
                                            : isHoliday
                                              ? `Holiday (${holidayLabel}): entry disabled.`
                                              : '\u00a0'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Box className={classes.filterFieldWrap}>
                                    <Typography className={classes.fieldLabel}>
                                        <SchoolIcon style={{ fontSize: 16 }} /> Class
                                    </Typography>
                                    <FormControl fullWidth variant="outlined" size="small" className={classes.filterSelect}>
                                        <Select displayEmpty value={classValue} onChange={getStudentList}>
                                            <MenuItem value=""><em>Select Class</em></MenuItem>
                                            {addClassList.map(({ _id, className }) => (
                                                <MenuItem key={_id} value={_id}>{className}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Typography className={classes.filterHelperSlot} style={{ color: 'transparent' }}>
                                        {'\u00a0'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Box className={classes.filterFieldWrap}>
                                    <Typography className={classes.fieldLabel}>
                                        <PersonIcon style={{ fontSize: 16 }} /> Section
                                    </Typography>
                                    <FormControl fullWidth variant="outlined" size="small" className={classes.filterSelect} disabled={!classValue || sectionList.length === 0}>
                                        <Select displayEmpty value={sectionValue} onChange={getSectionChange}>
                                            <MenuItem value=""><em>All Sections</em></MenuItem>
                                            {sectionList.map(({ _id, sectionName }) => (
                                                <MenuItem key={_id} value={_id}>{sectionName}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Typography className={classes.filterHelperSlot} style={{ color: 'transparent' }}>
                                        {'\u00a0'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Box className={classes.filterFieldWrap}>
                                    <Box style={{ minHeight: 18, marginBottom: 6 }} />
                                    <Box display="flex" justifyContent="flex-end">
                                        <Button className={classes.outlinedBtn} variant="outlined" onClick={handleResetFilters}>
                                            Reset
                                        </Button>
                                    </Box>
                                    <Typography className={classes.filterHelperSlot} style={{ color: 'transparent' }}>
                                        {'\u00a0'}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Collapse>
            </Card>

            {/* Table card */}
            <Card className={classes.tableCard} elevation={0}>
                <Box className={classes.toolbar}>
                    <Box display="flex" alignItems="center" gridGap={10} flexWrap="wrap">
                        <Typography variant="body2" style={{ color: '#46566b', fontWeight: 600 }}>
                            {filteredStudents.length} {filteredStudents.length === 1 ? 'Student' : 'Students'}
                        </Typography>
                        {studentList.length > 0 && (
                            <>
                                <Chip size="small" label={`Present ${presentCount}`} className={classes.presentChip} />
                                <Chip size="small" label={`Absent ${absentCount}`} className={classes.absentChip} />
                            </>
                        )}
                        {hasMarkedAttendance && (
                            <Chip
                                size="small"
                                label={`Created By: ${getCreatedByLabel(todayAttendanceCreatedBy)}`}
                                style={{ backgroundColor: '#eef4ff', color: '#1769d6', fontWeight: 600 }}
                            />
                        )}
                    </Box>
                    <Box display="flex" alignItems="center" gridGap={10} flexWrap="wrap">
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Search by student name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ minWidth: 240 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" style={{ color: '#9aa6b6' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Tooltip title="Refresh">
                            <span>
                                <IconButton
                                    className={classes.iconBtnOutlined}
                                    onClick={() => classValue && fetchStudentList(classValue, dateValue, sectionValue || undefined)}
                                    disabled={!classValue}
                                >
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>

                <TableContainer component={Paper} elevation={0} style={{ maxHeight: 540 }}>
                    <Table stickyHeader className="mb-0 dashboard-table-row-hover">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ ...headerCellStyle, width: 64 }} padding="checkbox" align="center">
                                    <Tooltip title={allFilteredPresent ? 'Unselect all' : 'Mark all present'}>
                                        <span>
                                            <Checkbox
                                                color="primary"
                                                size="small"
                                                checked={allFilteredPresent}
                                                indeterminate={someFilteredPresent}
                                                onChange={handleSelectAll}
                                                disabled={!editable || filteredStudents.length === 0}
                                            />
                                        </span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell style={headerCellStyle}>Profile</TableCell>
                                <TableCell style={headerCellStyle}>Student Name</TableCell>
                                <TableCell style={headerCellStyle}>Status</TableCell>
                                <TableCell style={headerCellStyle} align="center">Present</TableCell>
                                <TableCell style={headerCellStyle} align="center">Late</TableCell>
                                <TableCell style={headerCellStyle} align="center">Early</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredStudents.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" style={{ padding: 36, color: '#9aa6b6' }}>
                                        {classValue ? 'No students found.' : 'Select a class to load students.'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredStudents.map((student) => (
                                    <TableRow key={student._id} style={!student.status ? { backgroundColor: '#fff8f8' } : undefined}>
                                        <TableCell padding="checkbox" align="center">
                                            <Checkbox
                                                color="primary"
                                                checked={student.status}
                                                onChange={() => handleCheck(student._id)}
                                                disabled={!editable}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Avatar
                                                src={student.profileURL || student.imageURL?.[0] || undefined}
                                                className={classes.studentAvatar}
                                            >
                                                {(student.firstName || '?').charAt(0).toUpperCase()}
                                            </Avatar>
                                        </TableCell>
                                        <TableCell style={{ fontWeight: 600, color: '#0f2d4d' }}>
                                            {student.firstName} {student.lastName}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={student.status ? 'Present' : 'Absent'}
                                                className={student.status ? classes.presentChip : classes.absentChip}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Checkbox
                                                color="primary"
                                                checked={student.status}
                                                onChange={() => handleCheck(student._id)}
                                                disabled={!editable}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Checkbox
                                                checked={Boolean(student.lateComing)}
                                                onChange={() => handleLateComing(student._id)}
                                                disabled={!student.status || !editable}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Checkbox
                                                checked={Boolean(student.earlyGoing)}
                                                onChange={() => handleEarlyGoing(student._id)}
                                                disabled={!student.status || !editable}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Footer actions */}
                {studentList.length > 0 && (
                    <Box className={classes.footerBar}>
                        <Typography variant="caption" style={{ color: '#6b7280' }}>
                            {hasMarkedAttendance
                                ? `Attendance is already marked for ${dateValue}. Click Edit to update.`
                                : `Marking attendance for ${dateValue}.`}
                        </Typography>
                        <Box display="flex" alignItems="center" gridGap={10} flexWrap="wrap">
                            {!hasMarkedAttendance ? (
                                <Button
                                    className={classes.primaryBtn}
                                    variant="contained"
                                    startIcon={!submitLoading ? <CheckCircleIcon /> : null}
                                    onClick={handleSubmit}
                                    disabled={submitLoading || isAttendanceBlocked}
                                >
                                    {submitLoading ? (
                                        <>
                                            <CircularProgress size={18} style={{ marginRight: 8, color: 'white' }} />
                                            Submitting...
                                        </>
                                    ) : (
                                        isWeekend(dateValue)
                                            ? 'Weekend (disabled)'
                                            : isHoliday
                                              ? 'Holiday (disabled)'
                                              : 'Submit Attendance'
                                    )}
                                </Button>
                            ) : !isTodayEditMode ? (
                                <Button
                                    className={classes.outlinedBtn}
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={startTodayEdit}
                                    disabled={!todayAttendanceId}
                                >
                                    Edit
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        className={classes.outlinedBtn}
                                        variant="outlined"
                                        startIcon={<CancelIcon />}
                                        onClick={cancelTodayEdit}
                                        disabled={todayEditSaving}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        className={classes.primaryBtn}
                                        variant="contained"
                                        onClick={saveTodayEdit}
                                        disabled={todayEditSaving}
                                    >
                                        {todayEditSaving ? (
                                            <>
                                                <CircularProgress size={18} style={{ marginRight: 8, color: 'white' }} />
                                                Saving...
                                            </>
                                        ) : (
                                            'Save Changes'
                                        )}
                                    </Button>
                                </>
                            )}
                        </Box>
                    </Box>
                )}
            </Card>

            {/* Summary cards */}
            {todayAttendanceSummary.attendenceReport.length > 0 && (
                <Grid container spacing={2} style={{ marginTop: 16 }}>
                    <Grid item xs={6} sm={3}>
                        <Card className={classes.tableCard} elevation={0} style={{ padding: 16, textAlign: 'center' }}>
                            <Typography className={classes.statLabel} style={{ marginTop: 0 }}>Present</Typography>
                            <Typography style={{ color: '#1a9b54', fontWeight: 700, fontSize: '1.8rem' }}>
                                {todayAttendanceSummary.presentStudents}
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Card className={classes.tableCard} elevation={0} style={{ padding: 16, textAlign: 'center' }}>
                            <Typography className={classes.statLabel} style={{ marginTop: 0 }}>Absent</Typography>
                            <Typography style={{ color: '#c62828', fontWeight: 700, fontSize: '1.8rem' }}>
                                {todayAttendanceSummary.absentStudents}
                            </Typography>
                        </Card>
                    </Grid>
                </Grid>
            )}
        </>
    );
}
